/**
 * WCC360 v2 — how the ported v1 instrument is served.
 *
 * The v1 test was 46 trait items and 46 quiz items, one per screen, and it
 * took twelve minutes. Serving 92 more single-item screens on top of v2's
 * flow would be a worse experience than v1's, not a better one. So the port
 * keeps every item verbatim and changes only the *packaging*: items are served
 * in blocks of six (traits) and four (quizzes), each block on one screen with
 * one Continue.
 *
 * Blocks are derived from the item bank at module load, so adding an item to
 * the bank adds it to a screen automatically — there is no second list to keep
 * in sync.
 */
import type { Band } from "../band";
import {
  weekitQuestionsOrdered,
  type AgeGroup,
  type WeKitQuestion,
} from "./traitBank";
import { ALL_QUIZZES, type Quiz, type QuizQuestion } from "./nibble";

/** v2 bands ↔ the three v1 stems. Same item, age-appropriate wording. */
export function ageGroupFor(band: Band): AgeGroup {
  if (band === "12to14") return "12-14";
  if (band === "15to18") return "15-18";
  return "18+";
}

export const TRAIT_ITEMS_PER_SCREEN = 6;
export const QUIZ_ITEMS_PER_SCREEN = 4;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export interface TraitBlock {
  screenId: string;
  index: number;
  items: WeKitQuestion[];
}

/**
 * Trait blocks, in v1's interleaved order — consecutive items never share a
 * trait, so a block never reads as three rewordings of one question.
 */
export const TRAIT_BLOCKS: TraitBlock[] = chunk(
  weekitQuestionsOrdered,
  TRAIT_ITEMS_PER_SCREEN,
).map((items, index) => ({
  screenId: `pv1_traits_${index + 1}`,
  index,
  items,
}));

export interface QuizBlock {
  screenId: string;
  quizId: Quiz["id"];
  quizTitle: string;
  emoji: string;
  index: number;
  /** True on the first block of a quiz — the only one that shows the intro. */
  first: boolean;
  items: QuizQuestion[];
}

export const QUIZ_BLOCKS: QuizBlock[] = ALL_QUIZZES.flatMap((quiz) =>
  chunk(quiz.questions, QUIZ_ITEMS_PER_SCREEN).map((items, index) => ({
    screenId: `pv1_${quiz.id}_${index + 1}`,
    quizId: quiz.id,
    quizTitle: quiz.title,
    emoji: quiz.emoji,
    index,
    first: index === 0,
    items,
  })),
);

export const TRAIT_SCREEN_IDS = TRAIT_BLOCKS.map((b) => b.screenId);
export const QUIZ_SCREEN_IDS = QUIZ_BLOCKS.map((b) => b.screenId);
export const V1PORT_SCREEN_IDS = [...TRAIT_SCREEN_IDS, ...QUIZ_SCREEN_IDS];

export function traitBlockFor(screenId: string): TraitBlock | undefined {
  return TRAIT_BLOCKS.find((b) => b.screenId === screenId);
}

export function quizBlockFor(screenId: string): QuizBlock | undefined {
  return QUIZ_BLOCKS.find((b) => b.screenId === screenId);
}

export function quizById(id: Quiz["id"]): Quiz {
  return ALL_QUIZZES.find((q) => q.id === id)!;
}

/** Store keys the two block families write under. */
export const TRAIT_ANSWERS_KEY = "v1_trait_answers";
export const QUIZ_ANSWERS_KEY = "v1_quiz_answers";
